import { CheckSquare, Plus } from "lucide-react";
import type { Identifier } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { TASK_GROUPS, taskGroup, type GulaTask } from "../gulaTasks";
import type { GulaClient } from "../gulaTypes";
import { fullName } from "../gulaTypes";
import { NewTaskDialog } from "../NewTaskDialog";
import { TaskRow } from "../TaskRow";
import type { Agent } from "../useAgents";

type Scope = "mine" | "all" | "unassigned";

const SCOPES: { value: Scope; label: string }[] = [
  { value: "mine", label: "Mías" },
  { value: "all", label: "Todo el equipo" },
  { value: "unassigned", label: "Sin asignar" },
];

/** Agenda of open tasks grouped by when they are due. */
export const HomeTasks = ({
  tasks,
  clients,
  agents,
  meId,
  onChanged,
}: {
  tasks: GulaTask[];
  clients: GulaClient[];
  agents: Agent[];
  meId?: Identifier;
  onChanged: () => void;
}) => {
  const [scope, setScope] = useState<Scope>("mine");
  const [isNewOpen, setNewOpen] = useState(false);
  const clientByContact = new Map(
    clients.map((client) => [String(client.contact_id), client]),
  );

  const visible = tasks.filter((task) => {
    if (scope === "mine") return String(task.sales_id) === String(meId);
    if (scope === "unassigned") return task.sales_id == null;
    return true;
  });
  const countFor = (value: Scope) =>
    tasks.filter((task) =>
      value === "mine"
        ? String(task.sales_id) === String(meId)
        : value === "unassigned"
          ? task.sales_id == null
          : true,
    ).length;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <CheckSquare className="h-5 w-5 text-muted-foreground" />
        <h2 className="flex-1 text-lg font-semibold">Tareas</h2>
        <Button size="sm" onClick={() => setNewOpen(true)}>
          <Plus className="h-4 w-4" />
          Nueva tarea
        </Button>
      </div>
      <div
        className="flex gap-1 rounded-lg bg-muted p-1 text-sm"
        role="tablist"
      >
        {SCOPES.map((option) => (
          <button
            key={option.value}
            role="tab"
            aria-selected={scope === option.value}
            onClick={() => setScope(option.value)}
            className={cn(
              "flex-1 rounded-md px-2 py-1",
              scope === option.value
                ? "bg-background font-medium shadow-sm"
                : "text-muted-foreground",
            )}
          >
            {option.label} ({countFor(option.value)})
          </button>
        ))}
      </div>
      <Card className="rounded-3xl px-4 py-2">
        {visible.length ? (
          TASK_GROUPS.map((group) => {
            const items = visible.filter(
              (task) => taskGroup(task.due_date) === group.key,
            );
            if (!items.length) return null;
            return (
              <div key={group.key} className="py-1">
                <h3
                  className={cn(
                    "pt-2 text-xs font-semibold uppercase tracking-wide",
                    group.key === "overdue"
                      ? "text-destructive"
                      : "text-muted-foreground",
                  )}
                >
                  {group.label} · {items.length}
                </h3>
                <div className="divide-y">
                  {items.map((task) => {
                    const client = clientByContact.get(String(task.contact_id));
                    return (
                      <TaskRow
                        key={task.id}
                        task={task}
                        contactName={client ? fullName(client) : undefined}
                        clientId={client?.id as string | number | undefined}
                        agents={agents}
                        onChanged={onChanged}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })
        ) : (
          <p className="py-4 text-sm text-muted-foreground">
            {scope === "mine"
              ? "No tienes tareas pendientes."
              : "No hay tareas pendientes."}
          </p>
        )}
      </Card>
      <NewTaskDialog
        open={isNewOpen}
        onClose={() => setNewOpen(false)}
        onCreated={() => {
          setNewOpen(false);
          onChanged();
        }}
        clients={clients}
      />
    </section>
  );
};
